import { APP_BASE_HREF } from '@angular/common';
import {
    AfterViewInit, Component,
    ElementRef, EventEmitter, Inject,
    Input,
    OnDestroy,
    OnInit, Output, QueryList, ViewChildren
} from '@angular/core';

import { Subscription, tap } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import { MediaTypeEnum } from '../../../../Common/Basic/enums/media-type.enum';
import { FilterListProviderServiceInterface } from '../../../../Common/Filter/services/filter-list-provider-service.interface';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { JournodexRegionsService } from '../../../../Common/Journodex/services/journodex-regions.service';
import { AuditableModel } from '../../../../Common/Misc/models/auditable.model';
import { FilterTypeProviderServiceInterface } from '../../../../Common/MonitorShared/misc/filter-type-provider-service.interface';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { UserAppReportPreferencesModel } from '../../../../Core/Users/models/user-app-preferences.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { UsersService } from '../../../../Core/Users/services/users.service';
import { ChartColorPreferenceModel, ChartColorValue } from '../../models/chart-color-preferences.model';
import {
    MonitoringReportsDisplayFilterModel
} from '../../models/monitoring-display-filter.model';
import { SectionListItems } from '../UserPreferences/user-preferences.component';



// Utility type to allow the MediaTypes to be split up
type PartialMediaTypeColor = Partial<{-readonly [key in keyof ChartColorPreferenceModel['mediaTypeColors']]: ChartColorValue }>;
type PartialGeneralColor = Partial<{-readonly [key in keyof ChartColorPreferenceModel['generalColors']]: ChartColorValue }>;

@Component({
    selector: 'agpr-report-user-preferences',
    templateUrl: './report-user-preferences.component.html',
    styleUrls: ['./report-user-preferences.component.scss'],
    providers: [],
    standalone: false
})
export class ReportUserPreferencesComponent implements OnInit, AfterViewInit, OnDestroy{
    readonly buttonTypes = ButtonTypesEnum;

    @Input() reportPreferences: UserAppReportPreferencesModel;
    @Input() id: string;

    @Output() checkDirty = new EventEmitter<void>();
    @Output() sectionListItems = new EventEmitter<SectionListItems>();

    subscriptions: Subscription[] = [];

    defaultReportsFilters: MonitoringReportsDisplayFilterModel;
    reports: AuditableModel[] = [];

    chartColors: ChartColorPreferenceModel;
    mediaTypeColorsLeft: PartialMediaTypeColor;
    mediaTypeColorsRight: PartialMediaTypeColor;
    generalColorsLeft: PartialGeneralColor;
    generalColorsRight: PartialGeneralColor;
    otherMediaColors: PartialMediaTypeColor;
    socialMediaColors: PartialMediaTypeColor;
    onlySocial: boolean = false;
    /** We want to hide tiktok values if not provisioned, see D23592 */
    hasTiktok: boolean = false;

    private afterView = false;

    @ViewChildren('section') set sections(list: QueryList<ElementRef>) {
        if (list) {
            this.sectionListItems.emit(
                list.toArray().map(item => ({
                    label: `UserPreferences.${item.nativeElement.id}`,
                    value: item
                }))
            );
        }
    }

    constructor(
        public journodexRegionsService: JournodexRegionsService,
        public localizationService: LocalizationService,
        protected usersService: UsersService,
        public coreDialogService: CoreDialogService,
        private currentUserStateService: CurrentUserStateService,
        private monitorConfigService: MonitorConfigService,
        @Inject('MonitoringLeftSidebarService') public monitoringLeftSidebarService: FilterTypeProviderServiceInterface,
        @Inject('ReportsService') public reportsService: FilterListProviderServiceInterface,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {}

    ngOnInit() {
        this.subscriptions.push(
            this.currentUserStateService.getPermissions().pipe(
                filter(x => !!x),
                tap(permissions => {
                    this.onlySocial = !permissions.includes(userPermissionsEnum.advancedMonitoring);
                }),
                switchMap(() => this.monitorConfigService.getAvailableMediaTypes())
            ).subscribe(mediaTypes => {
                this.hasTiktok = !!mediaTypes?.includes(MediaTypeEnum.TIKTOK);
                this.setupColorCharts();
            }),
            this.reportsService.getFilterListItems().pipe(
                filter(x => !!x)
            ).subscribe( reports => {
                this.reports = reports;
                this.defaultReportsFilters = new MonitoringReportsDisplayFilterModel(this.reportPreferences.defaultFilters);
            }),
        );
        this.setupColorCharts();
    }

    ngAfterViewInit(): void {
        this.afterView = true;
    }

    keys (obj: Object): string[]{
        return Object.keys(obj);
    }

    getUpdatedValues(): UserAppReportPreferencesModel {
        return {
            defaultFilters: this.defaultReportsFilters?.getFilterValues() ?? this.reportPreferences.defaultFilters,
            defaultChartColors: this.chartColors.getCurrentValues(),
        };
    }

    ngOnDestroy(){
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    updateDirtyFlag() {
        if (!this.afterView) {
            return;
        }
        this.checkDirty.emit();
    }

    private setupColorCharts() {
        this.chartColors = new ChartColorPreferenceModel(this.reportPreferences.defaultChartColors);

        this.generalColorsLeft = {
            color1: this.chartColors['generalColors'].color1,
            color2: this.chartColors['generalColors'].color2,
            color3: this.chartColors['generalColors'].color3,
            color4: this.chartColors['generalColors'].color4,
            color5: this.chartColors['generalColors'].color5,
            color6: this.chartColors['generalColors'].color6,
        };

        this.generalColorsRight = {
            color7: this.chartColors['generalColors'].color7,
            color8: this.chartColors['generalColors'].color8,
            color9: this.chartColors['generalColors'].color9,
            color10: this.chartColors['generalColors'].color10,
            color11: this.chartColors['generalColors'].color11,
            color12: this.chartColors['generalColors'].color12,
        };

        if (this.onlySocial) {
            this.mediaTypeColorsLeft = {
                facebook: this.chartColors['mediaTypeColors'].facebook,
                instagram: this.chartColors['mediaTypeColors'].instagram,
                twitter: this.chartColors['mediaTypeColors'].twitter,
                threads: this.chartColors['mediaTypeColors'].threads,
                wechat: this.chartColors['mediaTypeColors'].wechat,
                twitch: this.chartColors['mediaTypeColors'].twitch,
                tumblr: this.chartColors['mediaTypeColors'].tumblr,
                whatsapp: this.chartColors['mediaTypeColors'].whatsapp,
                other: this.chartColors['mediaTypeColors'].other,
            };
            this.mediaTypeColorsRight = {
                youtube: this.chartColors['mediaTypeColors'].youtube,
                reddit: this.chartColors['mediaTypeColors'].reddit,
                snapchat: this.chartColors['mediaTypeColors'].snapchat,
                linkedin: this.chartColors['mediaTypeColors'].linkedin,
                bluesky: this.chartColors['mediaTypeColors'].bluesky,
                sinaWeibo: this.chartColors['mediaTypeColors'].sinaWeibo,
                pinterest: this.chartColors['mediaTypeColors'].pinterest,
                telegram: this.chartColors['mediaTypeColors'].telegram,
                vk: this.chartColors['mediaTypeColors'].vk,
            };
        } else {
            this.mediaTypeColorsLeft = {
                onlineNews: this.chartColors['mediaTypeColors'].onlineNews,
                pressRelease: this.chartColors['mediaTypeColors'].pressRelease,
                TV: this.chartColors['mediaTypeColors'].TV,
                podcast: this.chartColors['mediaTypeColors'].podcast,
            };
            this.mediaTypeColorsRight = {
                print: this.chartColors['mediaTypeColors'].print,
                blogs: this.chartColors['mediaTypeColors'].blogs,
                radio: this.chartColors['mediaTypeColors'].radio,
            };
        }

        this.otherMediaColors = {
            magazine: this.chartColors['mediaTypeColors'].magazine,
            sinaWeibo: this.chartColors['mediaTypeColors'].sinaWeibo,
            snapchat: this.chartColors['mediaTypeColors'].snapchat,
            telegram: this.chartColors['mediaTypeColors'].telegram,
            threads: this.chartColors['mediaTypeColors'].threads,
            tumblr: this.chartColors['mediaTypeColors'].tumblr,
            twitch: this.chartColors['mediaTypeColors'].twitch,
            wechat: this.chartColors['mediaTypeColors'].wechat,
            whatsapp: this.chartColors['mediaTypeColors'].whatsapp,
            vk: this.chartColors['mediaTypeColors'].vk,
            other: this.chartColors['mediaTypeColors'].other,
        };

        this.socialMediaColors = {
            facebook: this.chartColors['mediaTypeColors'].facebook,
            twitter: this.chartColors['mediaTypeColors'].twitter,
            instagram: this.chartColors['mediaTypeColors'].instagram,
            youtube: this.chartColors['mediaTypeColors'].youtube,
            reddit: this.chartColors['mediaTypeColors'].reddit,
            tiktok: this.chartColors['mediaTypeColors'].tiktok,
            linkedin: this.chartColors['mediaTypeColors'].linkedin,
            bluesky: this.chartColors['mediaTypeColors'].bluesky
        };

    }
}
